import React from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Container, Stack } from 'react-bootstrap';

import api from 'api';
import useMountEffect from 'hooks/useMountEffect';
import useRequest from 'hooks/useRequest';
import { DataUploadLabels } from 'utils/enums';
import { formatUTCDate } from 'utils/dateTime';

import SectionWrapper from 'components/layout/SectionWrapper';
import { HistoryBackButton } from 'components/elements/BackButton';
import Loader from 'components/elements/Loader';

interface FileMetadata {
  'x-amz-meta-feedback-total-records'?: string;
  'x-amz-meta-feedback-missing-dish'?: string;
  'x-amz-meta-feedback-admit-min'?: string;
  'x-amz-meta-feedback-admit-max'?: string;
  'x-amz-meta-feedback-missing-pt-age'?: string;
  'x-amz-meta-feedback-missing-pt-sex'?: string;
  'x-amz-meta-feedback-missing-dt'?: string;
}

interface FileUploadFeedbackProps {
  metadata?: FileMetadata;
}

const formatFeedbackDate = (dateStr: string) => dateStr.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3');

const FileUploadFeedback: React.FC<FileUploadFeedbackProps> = ({ metadata }) => {
  if (!metadata?.['x-amz-meta-feedback-total-records']) return null;

  return (
    <Stack gap={2}>
      <h5 className="mt-3 mb-0">Feedback</h5>
      <Stack direction="horizontal" gap={2}>
        <b>Total records received:</b>
        <span>{metadata['x-amz-meta-feedback-total-records']}</span>
      </Stack>

      {(metadata['x-amz-meta-feedback-admit-min']
      || metadata['x-amz-meta-feedback-admit-max']) && (
        <Stack direction="horizontal" gap={2}>
          <b>Admission date range:</b>
          <span>
            {`${formatFeedbackDate(metadata['x-amz-meta-feedback-admit-min']!)} - ${formatFeedbackDate(
              metadata['x-amz-meta-feedback-admit-max']!,
            )}`}
          </span>
        </Stack>
      )}

      <i className="mt-3">
        There are currently four fields used for inclusion in benchmark reports
        (more are used for risk adjusted models).
      </i>
      <i className="mb-3">A summary of the four fields is listed below:</i>

      <Stack direction="horizontal" gap={2}>
        <b>Cases missing patient age:</b>
        <span>{metadata['x-amz-meta-feedback-missing-pt-age']}</span>
      </Stack>
      <Stack direction="horizontal" gap={2}>
        <b>Cases missing patient sex:</b>
        <span>{metadata['x-amz-meta-feedback-missing-pt-sex']}</span>
      </Stack>
      <Stack direction="horizontal" gap={2}>
        <b>Cases missing admission date:</b>
        <span>{metadata['x-amz-meta-feedback-missing-dt']}</span>
      </Stack>
      <Stack direction="horizontal" gap={2}>
        <b>Cases missing discharge disposition:</b>
        <span>{metadata['x-amz-meta-feedback-missing-dish']}</span>
      </Stack>
    </Stack>
  );
};

interface UploadData {
  burnCenterName: string;
  description: string;
  createdAt: string;
  userEmail: string;
  type: keyof typeof DataUploadLabels;
}

interface FileData {
  id: string;
  filename: string;
  metadata?: FileMetadata;
}

interface FilesResponse {
  data: FileData[];
}

const UploadInfoContent: React.FC = () => {
  const { uploadId } = useParams<{ uploadId: string }>();

  const {
    doRequest: fetchUpload,
    data: uploadDataTmp,
    error,
  } = useRequest(api.getUpload);
  const uploadData = uploadDataTmp as unknown as UploadData;

  const {
    doRequest: fetchFiles,
    data: filesDataTmp,
    error: filesError,
  } = useRequest(api.getUploadsFiles);
  const filesData = filesDataTmp as unknown as FilesResponse;

  useMountEffect(() => {
    fetchUpload(uploadId!);
    fetchFiles({ DataUploadId: uploadId });
  });

  if (error || filesError) {
    return (
      <Alert className="mt-3 d-flex justify-content-between" variant="danger">
        Error loading report
      </Alert>
    );
  }

  if (!(uploadData && filesData)) return <Loader />;

  return (
    <Stack gap={2}>
      <Stack direction="horizontal" gap={2}>
        <b>Burn Center</b>
        <span>{uploadData.burnCenterName}</span>
      </Stack>
      <Stack direction="horizontal" gap={2}>
        <b>Description:</b>
        <span>{uploadData.description}</span>
      </Stack>
      <Stack direction="horizontal" gap={2}>
        <b>Submitted Date:</b>
        <span>{formatUTCDate(uploadData.createdAt, 'YYYY-MM-DD LTS UTC')}</span>
      </Stack>
      <Stack direction="horizontal" gap={2}>
        <b>Submitted by:</b>
        <span>{uploadData.userEmail}</span>
      </Stack>
      <Stack direction="horizontal" gap={2}>
        <b>Type of Data:</b>
        <span>{DataUploadLabels[uploadData.type]}</span>
      </Stack>

      {filesData.data.length > 1 && (
        <b>
          <i>Submitted files</i>
        </b>
      )}

      <Stack gap={2}>
        {filesData.data.map(({ id, filename, metadata }) => (
          <Stack key={id} gap={2}>
            <Stack direction="horizontal" gap={2}>
              <b>Filename:</b>
              <span>{filename}</span>
            </Stack>
            <FileUploadFeedback metadata={metadata} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const UploadInfoPage: React.FC = () => (
  <SectionWrapper title="Upload Info">
    <div className="mb-2">
      <HistoryBackButton />
    </div>
    <Container className="p-3 bg-light" fluid>
      <UploadInfoContent />
    </Container>
  </SectionWrapper>
);

export default UploadInfoPage;
